import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { api } from "../api/client";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { usePopoverPlacement } from "../nav/usePopoverPlacement";
import { floatingSheet, theme } from "../theme";

import { cbPalette } from "../lib/colourBlind";
interface AlertGroup {
  id: string;
  type:
    | "auditor_finding"
    | "overdue_check"
    | "overdue_event"
    | "upcoming_event"
    | "intune_drift"
    | "control_gap"
    | "control_undecided"
    | "check_failed"
    | "tprm"
    | "gdpr";
  title: string;
  subtitle: string;
  score: number;
  route: string;
  count: number;
}

interface AlertsResponse {
  totalCount: number;
  alerts: AlertGroup[];
}

const TYPE_COLOR: Record<AlertGroup["type"], string> = {
  auditor_finding:   "var(--c-danger)",
  control_gap:       "var(--c-danger)",
  intune_drift:      "var(--c-warning)",
  overdue_check:     "var(--c-warning)",
  overdue_event:     "var(--c-warning)",
  upcoming_event:    "var(--c-accent)",
  control_undecided: "var(--c-muted)",
  check_failed:      "var(--c-muted)",
  tprm:              "var(--c-warning)",
  gdpr:              "var(--c-danger)",
};

const TYPE_DOT: Record<AlertGroup["type"], string> = cbPalette({
  auditor_finding:   "#ef4444",
  control_gap:       "#ef4444",
  intune_drift:      "#f59e0b",
  overdue_check:     "#f59e0b",
  overdue_event:     "#f59e0b",
  upcoming_event:    "#6366f1",
  control_undecided: "#94a3b8",
  check_failed:      "#94a3b8",
  tprm:              "#f59e0b",
  gdpr:              "#7c3aed",
});

export function AlertBell() {
  // Client paths from the API (alert.route) go through the portal-aware
  // navigate so the bell also works inside the consultant read-through.
  const navigate = usePortalNavigate();
  const [open, setOpen]   = useState(false);
  const [data, setData]   = useState<AlertsResponse | null>(null);
  const wrapperRef        = useRef<HTMLDivElement>(null);
  const buttonRef         = useRef<HTMLButtonElement>(null);
  const placement         = usePopoverPlacement(open, buttonRef);

  function fetchAlerts() {
    api.get<AlertsResponse>("/alerts").then(setData).catch(() => {});
  }

  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { setOpen(false); buttonRef.current?.focus(); }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const total = data?.totalCount ?? 0;
  const top5  = data?.alerts.slice(0, 5) ?? [];
  // Items in the groups that are not listed (the footer counts items, not groups).
  const hiddenItems = Math.max(0, total - top5.reduce((sum, g) => sum + g.count, 0));

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={total > 0 ? `${total} item${total !== 1 ? "s" : ""} requiring attention` : "No alerts"}
        aria-label={total > 0 ? `Alerts, ${total} item${total !== 1 ? "s" : ""} requiring attention` : "Alerts, none"}
        aria-haspopup="true"
        aria-expanded={open}
        style={{
          position: "relative",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "4px 6px",
          display: "flex",
          alignItems: "center",
          borderRadius: 3,
          transition: "background 0.15s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-panelAlt)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
      >
        <Bell size={18} style={{ color: total > 0 ? "var(--c-danger)" : "var(--c-muted)" }} />
        {total > 0 && (
          <span
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              background: "var(--c-danger)",
              color: "#fff",
              borderRadius: 2,
              minWidth: 15,
              height: 15,
              fontSize: 9,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 3px",
              lineHeight: 1,
              fontFamily: theme.font,
            }}
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            ...floatingSheet,
            zIndex: 2000,
            overflow: "hidden",
            fontFamily: theme.font,
            ...placement,
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: "10px 14px",
              borderBottom: "1px solid var(--c-line)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--c-heading)" }}>
              Action required
            </span>
            <span style={{ fontSize: 11, color: "var(--c-muted)" }}>
              {total} item{total !== 1 ? "s" : ""} total
            </span>
          </div>

          {/* Alert rows */}
          {top5.length === 0 ? (
            <div
              style={{
                padding: "20px 14px",
                textAlign: "center",
                color: "var(--c-muted)",
                fontSize: 12,
              }}
            >
              All caught up — no alerts.
            </div>
          ) : (
            top5.map((alert, i) => (
              <button
                key={alert.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(alert.route);
                }}
                style={{
                  display: "flex",
                  width: "100%",
                  padding: "9px 14px",
                  gap: 10,
                  background: "none",
                  border: "none",
                  borderBottom: i < top5.length - 1 ? "1px solid var(--c-border)" : "none",
                  cursor: "pointer",
                  textAlign: "left",
                  alignItems: "flex-start",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--c-panelAlt)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
              >
                {/* Coloured dot */}
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: TYPE_DOT[alert.type],
                    flexShrink: 0,
                    marginTop: 4,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: TYPE_COLOR[alert.type],
                      lineHeight: 1.35,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {alert.title}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--c-muted)",
                      marginTop: 2,
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {alert.subtitle}
                  </div>
                </div>
                {/* Arrow */}
                <span style={{ color: "var(--c-muted)", fontSize: 11, marginTop: 2, flexShrink: 0 }}>›</span>
              </button>
            ))
          )}

          {/* Footer — show overflow count */}
          {hiddenItems > 0 && (
            <div
              style={{
                padding: "7px 14px",
                borderTop: "1px solid var(--c-border)",
                fontSize: 11,
                color: "var(--c-muted)",
                textAlign: "center",
              }}
            >
              +{hiddenItems} more item{hiddenItems !== 1 ? "s" : ""} not shown
            </div>
          )}
        </div>
      )}
    </div>
  );
}
